import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableContainer,
  Paper,
  Chip,
  Link,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { useMediaQuery, useTheme } from "@mui/material";

const LegacyBookingTable = ({ orders }) => {
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const toggleExpanded = (id) => {
    setExpandedOrderId((prev) => (prev === id ? null : id));
  };

  if (!orders?.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">No legacy orders found.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 4 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {!isMobile && (
              <TableCell>
                <strong>Order ID</strong>
              </TableCell>
            )}
            <TableCell>
              <strong>Customer</strong>
            </TableCell>
            {!isMobile && (
              <TableCell>
                <strong>Phone</strong>
              </TableCell>
            )}
            <TableCell>
              <strong>Pickup</strong>
            </TableCell>
            <TableCell>
              <strong>Location</strong>
            </TableCell>
            {!isMobile && (
              <TableCell>
                <strong>Status</strong>
              </TableCell>
            )}
            {!isMobile && (
              <TableCell>
                <strong>Total</strong>
              </TableCell>
            )}
            {!isMobile && (
              <TableCell>
                <strong>Receipt</strong>
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {orders.map((order) => {
            const {
              _id,
              orderId,
              customerInfo,
              collectionDate,
              collectionTime,
              collectionLocation,
              orderStatus,
              total,
              stripeReceiptUrl,
              email,
            } = order;

            const customerName = customerInfo?.name || "N/A";
            const customerPhone = customerInfo?.phone || "—";
            const customerEmail = email || customerInfo?.email || "—";
            const pickupDate = collectionDate
              ? dayjs(collectionDate).format("MMM D, YYYY")
              : "—";

            const isToday =
              collectionDate && dayjs(collectionDate).isSame(dayjs(), "day");

            const rowStyle = isToday
              ? { backgroundColor: "#FFFBE6" } // soft yellow highlight
              : {};

            return (
              <React.Fragment key={_id}>
                <TableRow
                  hover
                  sx={{ cursor: "pointer", ...rowStyle }}
                  onClick={() => toggleExpanded(_id)}>
                  {!isMobile && <TableCell>{orderId || "—"}</TableCell>}

                  <TableCell>
                    <Tooltip title={customerEmail}>
                      <span>{customerName}</span>
                    </Tooltip>
                    <Typography variant="caption" display="block">
                      {customerEmail}
                    </Typography>
                  </TableCell>

                  {!isMobile && <TableCell>{customerPhone}</TableCell>}

                  <TableCell sx={{ minWidth: 100 }}>
                    {pickupDate}
                    <br />
                    <Typography variant="caption">
                      {collectionTime || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ minWidth: 100 }}>
                    {collectionLocation || "—"}
                  </TableCell>

                  {!isMobile && (
                    <TableCell>
                      <Chip
                        size="small"
                        label={orderStatus || "—"}
                        color={
                          orderStatus === "complete"
                            ? "success"
                            : orderStatus === "in-progress"
                            ? "warning"
                            : orderStatus === "cancelled"
                            ? "error"
                            : "default"
                        }
                      />
                    </TableCell>
                  )}

                  {!isMobile && (
                    <TableCell>
                      {typeof total === "number" ? `$${total.toFixed(2)}` : "—"}
                    </TableCell>
                  )}

                  {!isMobile && (
                    <TableCell>
                      {stripeReceiptUrl ? (
                        <Link
                          href={stripeReceiptUrl}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                          onClick={(e) => e.stopPropagation()}>
                          View
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>

                {expandedOrderId === _id && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{ backgroundColor: "#fafafa", px: 3, py: 2 }}>
                      {(order.products || []).map((p, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            borderBottom: "1px solid #eee",
                            py: 1,
                          }}>
                          {p.imgPath ? (
                            <Box
                              component="img"
                              src={p.imgPath}
                              alt={p.name}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 1,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: "#eee",
                                borderRadius: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                color: "#888",
                              }}>
                              No Image
                            </Box>
                          )}

                          <Box>
                            {isMobile && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  Order Info
                                </Typography>
                                <Typography variant="body2">
                                  Order ID: {orderId || "—"}
                                </Typography>
                                <Typography variant="body2">
                                  Phone: {customerPhone}
                                </Typography>
                                <Typography variant="body2">
                                  Status: {orderStatus || "—"}
                                </Typography>
                                <Typography variant="body2">
                                  Total:{" "}
                                  {typeof total === "number"
                                    ? `$${total.toFixed(2)}`
                                    : "—"}
                                </Typography>
                                <Typography variant="body2">
                                  Receipt:{" "}
                                  {stripeReceiptUrl ? (
                                    <Link
                                      href={stripeReceiptUrl}
                                      target="_blank"
                                      rel="noopener"
                                      underline="hover"
                                      onClick={(e) => e.stopPropagation()}>
                                      View
                                    </Link>
                                  ) : (
                                    "—"
                                  )}
                                </Typography>
                              </Box>
                            )}

                            <Typography variant="body2" fontWeight="bold">
                              {p.name || "Unnamed"}{" "}
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                {p.category || ""}
                              </Typography>
                            </Typography>
                            <Typography variant="body2">
                              {p.size || ""} • {p.flavor || ""} • Qty:{" "}
                              {p.quantity || 1}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ${p.price?.toFixed(2) || "—"}
                            </Typography>
                          </Box>
                        </Box>
                      ))}

                      {(order.addOns || []).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Add-Ons
                          </Typography>
                          {(order.addOns || []).map((addon, i) => (
                            <Typography key={i} variant="body2">
                              {addon.name}: {addon.text || ""}{" "}
                              {addon.price ? `($${addon.price})` : ""}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {order.additionalNotes &&
                        order.additionalNotes !== "n/a" && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                              Additional Notes
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: "pre-wrap" }}>
                              {order.additionalNotes}
                            </Typography>
                          </Box>
                        )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LegacyBookingTable;
